import { useState, type FormEvent } from 'react'
import { Files } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import { useCreateForm } from '@/features/forms/useForms'
import { useCreateList } from '@/features/lists/useLists'
import { inputOf, retarget } from '@/features/forms/formBuilder'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import type { Form } from '@/api/forms'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/**
 * Duplicate a form — typically the last off-market form, for the next property: swap the old address
 * for the new one everywhere in the form, and give the copy its own enquirers list so the two
 * campaigns' people don't mix. The copy starts paused, to check before it goes live.
 */
export default function DuplicateFormModal({ form, onClose, onCreated }: { form: Form; onClose: () => void; onCreated: (f: Form) => void }) {
  const create = useCreateForm()
  const createList = useCreateList()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [ownList, setOwnList] = useState(!!form.addToListId)
  const [error, setError] = useState<string | null>(null)
  const busy = create.isPending || createList.isPending

  const base = retarget(inputOf(form, { enabled: false, listingDealId: null }), from, to)
  const name = from.trim() && to.trim() && base.name !== form.name ? base.name : `${form.name} (copy)`

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      let addToListId = base.addToListId ?? null
      if (ownList) {
        const list = await createList.mutateAsync({ name: `${name} – enquirers`, listType: 'manual', description: `Everyone who fills in “${name}”.` })
        addToListId = list.id
      }
      const copy = await create.mutateAsync({ ...base, name, addToListId })
      onCreated(copy)
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  return (
    <Modal
      title="Duplicate form"
      icon={<Files size={17} className="text-brand-primary" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={modalButton.ghost}>Cancel</button>
          <button type="submit" form="duplicate-form" disabled={busy} className={modalButton.primary}>{busy ? 'Duplicating…' : 'Duplicate'}</button>
        </>
      }
    >
      <form id="duplicate-form" onSubmit={submit} className="space-y-3">
        <p className="text-sm text-gray-600">A copy of <b>{form.name}</b> — fields, routing, look and after-submit steps.</p>
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <div className="text-xs font-medium text-gray-500">For another property? Swap the address everywhere in the form</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <input className={inputCls} aria-label="Replace" placeholder="Replace… e.g. 39 Grand Parade, Ashgrove" value={from} onChange={(e) => setFrom(e.target.value)} />
            <input className={inputCls} aria-label="With" placeholder="With… e.g. 11 Hunter Street, Kelvin Grove" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div className="text-sm">
          <span className="text-gray-500">The copy will be called </span>
          <b className="text-brand-darkest">{name}</b>
          <span className="text-gray-500"> and start paused.</span>
        </div>
        {form.addToListId && (
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-0.5" checked={ownList} onChange={(e) => setOwnList(e.target.checked)} />
            <span>Give the copy its own list of enquirers (“{name} – enquirers”) instead of sharing this form’s list</span>
          </label>
        )}
        {error && <p className="text-danger text-xs">{error}</p>}
      </form>
    </Modal>
  )
}
